import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { joiResolver } from '@hookform/resolvers/joi';
import { useSnackbar } from 'notistack';
import { useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { generatePath, useNavigate } from 'react-router-dom';

import { usePartnersEventAction, usePartnersSaveEvent } from '@/api/hooks';
import { type Partner } from '@/api/routes/partners/partners.api';
import { type PartnerEventDetail } from '@/api/routes/partners/partners.events.api';
import Button from '@/components/Button';
import AddressFields from '@/components/forms/AddressFields';
import FormGlobalError from '@/components/forms/FormGlobalError';
import FormSectionHeader from '@/components/forms/FormSectionHeader';
import EventBannerField from '@/domain/events/components/EventBannerField';
import EventConfirmModal from '@/domain/events/components/EventConfirmModal';
import EventFormField from '@/domain/events/components/EventFormField';
import EventWhenFields from '@/domain/events/components/EventWhenFields';
import { buildEventSchema } from '@/domain/events/schemas';
import { type EventFormIntent, type EventFormValues } from '@/domain/events/types';
import {
  canSubmitFromForm,
  emptyEventFormValues,
  fromPartnerEvent,
  getEventFormSaveMode,
  getEventTypeOptions,
  isEventFormVisibleField,
  todayDateString,
  toPartnerEventBody,
} from '@/domain/events/utils';
import { useApiFormErrors, useServerErrorsStore } from '@/hooks/useApiFormErrors';
import { useTranslate } from '@/state/i18n';
import { ScreenRoutes } from '@/types/router';
import { withServerErrors } from '@/utils/errors';
import { radiusTokens } from '@/theme/themePrimitives';

interface Props {
  partner: Partner;
  /** The event being edited; a new event has none. */
  event?: PartnerEventDetail;
}

/**
 * P09 form, for a new event and for editing. How it saves depends on the status
 * (`getEventFormSaveMode`): drafts can be sent right away by an approved account, an event in
 * review stays in the queue, and a published one goes back to review after a confirmation (P14).
 */
const EventEditor = ({ partner, event }: Props) => {
  const t = useTranslate('domain.events.components.EventEditor');
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const mode = getEventFormSaveMode(event);
  const canSubmit = canSubmitFromForm(mode, partner.status);
  const intentRef = useRef<EventFormIntent>('save');
  const [confirmValues, setConfirmValues] = useState<EventFormValues | null>(null);

  const serverErrors = useServerErrorsStore();
  const resolver = useMemo(
    () =>
      withServerErrors(
        joiResolver(buildEventSchema({ today: event ? null : todayDateString() })),
        serverErrors,
      ),
    [event, serverErrors],
  );
  const {
    control,
    handleSubmit,
    setError,
    getValues,
    setValue,
    formState: { isDirty },
  } = useForm<EventFormValues>({
    resolver,
    mode: 'onTouched',
    defaultValues: event ? fromPartnerEvent(event) : emptyEventFormValues(),
  });
  const { applyApiError, globalError, clearGlobalError, clearApiErrors } =
    useApiFormErrors<EventFormValues>({
      setError,
      getValues,
      serverErrors,
      isVisibleField: isEventFormVisibleField,
      fallbackMessage: t('saveError'),
    });

  const { mutateAsync: save, isPending: isSaving } = usePartnersSaveEvent(event?.id);
  const { mutateAsync: submit, isPending: isSubmitting } = usePartnersEventAction('submit');
  const isBusy = isSaving || isSubmitting;

  const persist = async (values: EventFormValues, intent: EventFormIntent) => {
    clearApiErrors();
    let savedId: string;
    try {
      savedId = (await save(toPartnerEventBody(values))).event.id;
    } catch (error) {
      setConfirmValues(null);
      applyApiError(error);
      return;
    }

    if (intent === 'submit') {
      try {
        await submit(savedId);
        enqueueSnackbar(t('submitted'), { variant: 'success' });
      } catch {
        // Saved as a draft; the API reason (e.g. address not located) is in the global snackbar.
      }
    } else {
      enqueueSnackbar(t(`saved.${mode}`), { variant: 'success' });
    }
    navigate(generatePath(ScreenRoutes.EVENT_DETAIL, { eventId: savedId }), { replace: true });
  };

  const onValid = (values: EventFormValues) => {
    if (mode === 'approved') {
      setConfirmValues(values);
      return;
    }
    return persist(values, intentRef.current);
  };

  const submitWith = (intent: EventFormIntent) => () => {
    intentRef.current = intent;
  };

  const cancelEdit = () =>
    navigate(
      event ? generatePath(ScreenRoutes.EVENT_DETAIL, { eventId: event.id }) : ScreenRoutes.EVENTS,
    );

  return (
    <Box component="form" noValidate onSubmit={handleSubmit(onValid)} sx={sx.root}>
      <Stack gap={0.75}>
        <Typography component="h1" variant="h2">
          {event ? t('editTitle') : t('createTitle')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('subtitle')}
        </Typography>
      </Stack>

      {mode === 'approved' ? (
        <Alert severity="warning">
          <AlertTitle>{t('approvedWarningTitle')}</AlertTitle>
          {t('approvedWarning')}
        </Alert>
      ) : null}
      {event?.reviewStatus === 'rejected' && event.lastReviewNote ? (
        <Alert severity="error">
          <AlertTitle>{t('rejectedNoteTitle')}</AlertTitle>
          {event.lastReviewNote}
        </Alert>
      ) : null}
      <FormGlobalError message={globalError} onClose={clearGlobalError} />

      <Paper variant="outlined" sx={sx.paper}>
        <Stack divider={<Box sx={sx.divider} />}>
          <Box sx={sx.section}>
            <FormSectionHeader n={1} title={t('sections.banner')} hint={t('optional')} />
            <EventBannerField control={control} />
          </Box>
          <Box sx={sx.section}>
            <FormSectionHeader n={2} title={t('sections.info')} />
            <Stack gap={2.5}>
              <Box sx={sx.nameRow}>
                <EventFormField control={control} name="name" label={t('name')} />
                <EventFormField
                  control={control}
                  name="eventType"
                  label={t('eventType')}
                  select
                  slotProps={{ select: { displayEmpty: true } }}
                >
                  <MenuItem value="">{t('noEventType')}</MenuItem>
                  {getEventTypeOptions(event?.eventType ?? undefined).map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </EventFormField>
              </Box>
              <EventFormField
                control={control}
                name="description"
                label={t('description')}
                optionalLabel={t('optional')}
                placeholder={t('descriptionPlaceholder')}
                multiline
                minRows={4}
              />
            </Stack>
          </Box>
          <Box sx={sx.section}>
            <FormSectionHeader n={3} title={t('sections.when')} />
            <EventWhenFields control={control} minDate={event ? undefined : todayDateString()} />
          </Box>
          <Box sx={sx.section}>
            <FormSectionHeader n={4} title={t('sections.where')} hint={t('whereHint')} />
            <AddressFields control={control} setValue={setValue} name="address" />
          </Box>
          <Box sx={sx.section}>
            <FormSectionHeader n={5} title={t('sections.link')} hint={t('optional')} />
            <EventFormField
              control={control}
              name="externalUrl"
              label={t('externalUrl')}
              placeholder="https://"
              inputMode="url"
              hint={t('externalUrlHint')}
            />
          </Box>
        </Stack>
      </Paper>

      <Box sx={sx.footer}>
        <Button variant="text" onClick={cancelEdit} disabled={isBusy}>
          {t('cancel')}
        </Button>
        <Stack direction={{ xs: 'column-reverse', sm: 'row' }} gap={1.5} alignItems="stretch">
          {mode === 'draft' ? (
            <>
              <Button
                type="submit"
                variant="outlined"
                onClick={submitWith('save')}
                loading={isSaving && intentRef.current === 'save'}
                disabled={isBusy || (Boolean(event) && !isDirty)}
              >
                {t('saveDraft')}
              </Button>
              <Button
                type="submit"
                variant="contained"
                onClick={submitWith('submit')}
                loading={isBusy && intentRef.current === 'submit'}
                disabled={!canSubmit || isBusy}
              >
                {t('saveAndSubmit')}
              </Button>
            </>
          ) : (
            <Button
              type="submit"
              variant="contained"
              onClick={submitWith('save')}
              loading={isBusy}
              disabled={!isDirty || isBusy}
            >
              {mode === 'approved' ? t('saveAndSubmit') : t('saveChanges')}
            </Button>
          )}
        </Stack>
      </Box>
      {mode === 'draft' && !canSubmit ? (
        <Typography variant="caption" color="text.secondary" textAlign="right">
          {t('submitLocked')}
        </Typography>
      ) : null}

      <EventConfirmModal
        open={Boolean(confirmValues)}
        title={t('resubmitModal.title')}
        message={t('resubmitModal.message')}
        confirmLabel={t('resubmitModal.confirm')}
        cancelLabel={t('resubmitModal.cancel')}
        loading={isSaving}
        onConfirm={() => confirmValues && persist(confirmValues, 'save')}
        onClose={() => setConfirmValues(null)}
      />
    </Box>
  );
};

export default EventEditor;

const sx = {
  root: {
    width: '100%',
    maxWidth: 880,
    mx: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: 3,
  },
  paper: { borderRadius: radiusTokens.xxl, overflow: 'hidden' },
  section: { p: { xs: 2.5, sm: 3.5 } },
  divider: { borderTop: 1, borderColor: 'divider' },
  nameRow: { display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '2fr 1fr' } },
  footer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: { xs: 'stretch', sm: 'center' },
    flexDirection: { xs: 'column-reverse', sm: 'row' },
    gap: 2,
  },
};
